import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "parentCategories",
    "subCategoriesContainer",
    "accreditations",
    "previewCount",
    "previewLoading"
  ]

  static values = {
    previewUrl: String,
    subcategoriesUrl: String
  }

  connect() {
    // Track previously selected parent categories
    this.previousParentIds = this.getSelectedParentIds()
  }

  // Load sub-categories when parent categories change
  async loadSubCategories() {
    const currentParentIds = this.getSelectedParentIds()
    
    if (currentParentIds.length === 0) {
      this.subCategoriesContainerTarget.innerHTML = this.emptySubCategoriesMessage()
      this.previousParentIds = []
      this.updatePreview()
      return
    }

    // Determine which parent categories are newly added
    const newlyAddedParents = currentParentIds.filter(id => !this.previousParentIds.includes(id))
    
    // Get currently selected sub-category IDs before re-rendering
    const currentlySelectedSubIds = this.getSelectedSubCategoryIds()

    try {
      const params = new URLSearchParams()
      currentParentIds.forEach(id => params.append('parent_ids[]', id))

      const response = await fetch(`${this.subcategoriesUrlValue}?${params}`)
      const data = await response.json()

      // Render with preserved selections + auto-check for new parents
      this.renderSubCategories(data, currentlySelectedSubIds, newlyAddedParents)
      
      // Update tracking
      this.previousParentIds = currentParentIds
      
      this.updatePreview()
    } catch (error) {
      console.error('Error loading sub-categories:', error)
    }
  }

  // Get currently selected parent category IDs
  getSelectedParentIds() {
    if (!this.hasParentCategoriesTarget) return []
    
    return Array.from(this.parentCategoriesTarget.selectedOptions)
      .map(option => option.value)
  }

  // Get currently selected sub-category IDs
  getSelectedSubCategoryIds() {
    const checkboxes = this.element.querySelectorAll(
      'input[name="talent_pool[smart_criteria][sub_category_ids][]"]:checked'
    )
    return Array.from(checkboxes).map(cb => cb.value)
  }

  // Render sub-categories grouped by parent
  renderSubCategories(groupedData, selectedSubIds = [], newlyAddedParents = []) {
    if (Object.keys(groupedData).length === 0) {
      this.subCategoriesContainerTarget.innerHTML = this.emptySubCategoriesMessage()
      return
    }

    let html = '<div class="form-group">'
    html += '<label class="control-label">Specific Skills</label>'

    Object.entries(groupedData).forEach(([parentName, parentData]) => {
      const parentId = parentData.parent_id
      const subCats = parentData.sub_categories
      const isNewParent = newlyAddedParents.includes(parentId.toString())

      html += '<div class="panel panel-default">'
      html += `<div class="panel-heading"><strong><i class="fa fa-folder-open"></i> ${this.escapeHtml(parentName)}</strong></div>`
      html += '<div class="panel-body"><div class="row">'

      subCats.forEach(subCat => {
        // Check if this sub-category should be checked:
        // 1. It was previously selected, OR
        // 2. Its parent is newly added (auto-select all)
        const wasSelected = selectedSubIds.includes(subCat.id.toString())
        const shouldBeChecked = wasSelected || isNewParent

        html += '<div class="col-md-6"><div class="checkbox"><label>'
        html += `<input type="checkbox" name="talent_pool[smart_criteria][sub_category_ids][]" value="${subCat.id}" `
        html += 'data-action="change->talent-pool-smart-criteria#updatePreview"'
        if (shouldBeChecked) {
          html += ' checked'
        }
        html += '>'
        html += ` ${this.escapeHtml(subCat.name)}`
        html += '</label></div></div>'
      })

      html += '</div></div></div>'
    })

    html += '<p class="help-block"><i class="fa fa-info-circle"></i> Select specific skill sub-categories to include</p>'
    html += '</div>'

    this.subCategoriesContainerTarget.innerHTML = html
  }

  emptySubCategoriesMessage() {
    return `
      <div class="form-group">
        <div class="alert alert-info">
          <i class="fa fa-info-circle"></i>
          Select parent categories above to see specific skills
        </div>
      </div>
    `
  }

  // Update preview count
  async updatePreview() {
    const criteria = this.gatherCriteria()

    this.showLoading()

    try {
      const params = new URLSearchParams()
      
      if (criteria.parent_category_ids.length > 0) {
        criteria.parent_category_ids.forEach(id => 
          params.append('smart_criteria[parent_category_ids][]', id)
        )
      }
      
      if (criteria.sub_category_ids.length > 0) {
        criteria.sub_category_ids.forEach(id => 
          params.append('smart_criteria[sub_category_ids][]', id)
        )
      }
      
      if (criteria.accreditation_ids.length > 0) {
        criteria.accreditation_ids.forEach(id => 
          params.append('smart_criteria[accreditation_ids][]', id)
        )
      }

      const response = await fetch(`${this.previewUrlValue}?${params}`)
      const data = await response.json()

      this.updateCount(data.count)
    } catch (error) {
      console.error('Error updating preview:', error)
      this.updateCount('?')
    }
  }

  // Gather all selected criteria
  gatherCriteria() {
    const criteria = {
      parent_category_ids: [],
      sub_category_ids: [],
      accreditation_ids: []
    }

    // Parent categories
    if (this.hasParentCategoriesTarget) {
      criteria.parent_category_ids = Array.from(this.parentCategoriesTarget.selectedOptions)
        .map(option => option.value)
    }

    // Sub-categories (from dynamically rendered checkboxes)
    const subCategoryCheckboxes = this.element.querySelectorAll(
      'input[name="talent_pool[smart_criteria][sub_category_ids][]"]:checked'
    )
    criteria.sub_category_ids = Array.from(subCategoryCheckboxes)
      .map(checkbox => checkbox.value)

    // Accreditations
    if (this.hasAccreditationsTarget) {
      criteria.accreditation_ids = Array.from(this.accreditationsTarget.selectedOptions)
        .map(option => option.value)
    }

    return criteria
  }

  showLoading() {
    this.previewCountTarget.style.display = 'none'
    this.previewLoadingTarget.style.display = 'inline'
  }

  updateCount(count) {
    this.previewCountTarget.textContent = count
    this.previewCountTarget.style.display = 'inline'
    this.previewLoadingTarget.style.display = 'none'
  }

  // Utility to escape HTML
  escapeHtml(text) {
    const div = document.createElement('div')
    div.textContent = text
    return div.innerHTML
  }
}